// What the panel SAYS about the anchor, as pure functions.
//
// Same reason panelModel.ts exists: `frontend/vitest.config.ts` is
// `environment: 'node'` with `include: ['src/**/*.test.ts']` and there is no
// jsdom here, so a `.tsx` test would not be collected and would report a pass
// having run nothing. Every sentence with a decision in it therefore lives in
// a `.ts` module with real tests, and the components stay thin enough to read.
//
// These sentences were carried over from `VerificationBadge.tsx`, which T14
// deletes. They are not re-written on the way: the wording is the product's
// promise about what it does and does not claim, and it was settled once.
//
// NOTHING HERE READS A CHAIN OR STOPS PLAYBACK. It turns an already-read
// ChainRecord into words.
import type { AnchorState, ChainRecord } from './useChainRecord.js'
import type { VerifyState } from './useVideoVerification.js'

export function formatDate(date: Date | null): string {
  if (!date) return 'unknown time'
  return date.toLocaleString()
}

/**
 * Days-and-hours, or hours-and-minutes, or minutes. Minute-grained on purpose:
 * this is a courtesy countdown on a public page, and a seconds display would
 * imply a precision the local clock does not have.
 */
export function formatCountdown(remainingMs: number): string {
  if (remainingMs <= 0) return 'under a minute'
  const totalMinutes = Math.floor(remainingMs / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}, ${hours} hour${hours === 1 ? '' : 's'}`
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}, ${minutes} minute${minutes === 1 ? '' : 's'}`
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

/**
 * The headline: what the chain promises about this video.
 *
 * The anchor axis and the verify axis are INDEPENDENT and this returns only
 * the first. A video stays sealed, or stays correctable, whatever today's
 * content check found — gating one on the other would let a transient fetch
 * problem appear to unseal a video, which is the opposite of what a seal is.
 */
export function anchorHeadline(anchor: AnchorState, chain: ChainRecord | null): string {
  if (!chain || anchor === 'unanchored') return 'Not anchored'
  return anchor === 'sealed' ? 'Sealed' : 'Published, and still correctable'
}

/**
 * The line under the headline: when it sealed, or how long the publisher can
 * still correct it.
 *
 * `sealedAt` is null exactly when a video sealed because its correction window
 * expired rather than by an explicit seal call. Say that, rather than print a
 * seal timestamp that was never recorded.
 */
export function anchorDetail(anchor: AnchorState, chain: ChainRecord | null, nowMs: number): string {
  if (!chain || anchor === 'unanchored') {
    return 'There is no record of this video on the chain.'
  }
  if (anchor === 'sealed') {
    return chain.sealedAt
      ? `Sealed ${formatDate(chain.sealedAt)}. It can no longer be changed.`
      : `Sealed automatically when the correction window closed, ${formatDate(chain.windowExpires)}. It can no longer be changed.`
  }
  return `The publisher can still correct this video for ${formatCountdown(
    chain.windowExpires.getTime() - nowMs,
  )}, until ${formatDate(chain.windowExpires)}.`
}

/**
 * What the panel says about today's check, as opposed to the seal.
 *
 * Never a blanket "verified": the only claim this product makes is about the
 * slices it actually looked at, and the count beside the coverage bar is where
 * that claim lives. This line says what the checking is DOING.
 */
export function checkStatusText(verify: VerifyState, checkedCount: number): string {
  if (verify === 'failed') return 'A slice did not match the sealed record.'
  if (verify === 'idle' || verify === 'verifying') return 'Reading the sealed record from the chain.'
  if (checkedCount === 0) return 'Watching for slices to arrive. Nothing has been checked yet.'
  return 'Every slice checked so far matched the sealed record.'
}

/** Whether the countdown needs to tick. Only a published video has one. */
export function needsCountdown(anchor: AnchorState, chain: ChainRecord | null): boolean {
  return Boolean(chain) && anchor === 'published'
}
